import React, { useEffect, useRef } from "react";
import { Button } from "@mui/material";
import {
  ruleAccent,
  standingRuleLabel,
  signedDifference,
  formatGoalPair,
} from "./CompactLeagueTable";
import "./TeamLeagueWorkspace.css";
export default function WorkspaceStandingsTable({
  rows,
  data,
  visibleColumns,
  visibleMetrics,
  showStatistics,
  groups,
  columnClass,
  digits,
  labels,
  rulesFor,
  fmt,
  t,
  en,
  onTeam,
  selectedTeam,
}) {
  const root = useRef(null);
  useEffect(() => {
    if (selectedTeam)
      root.current
        ?.querySelector(`[data-team-id="${Number(selectedTeam)}"]`)
        ?.scrollIntoView?.({ block: "nearest" });
  }, [selectedTeam]);
  return (
    <div ref={root} className="tl-table">
      <table
        style={{
          width:
            40 +
            260 +
            visibleColumns.reduce(
              (n, k) =>
                n +
                (["played", "won", "draw", "lost"].includes(k)
                  ? 48
                  : k === "goals"
                  ? 104
                  : 88),
              0
            ) +
            (showStatistics ? groups.length * visibleMetrics.length * 132 : 0) +
            (onTeam ? 120 : 0),
        }}
      >
        <colgroup>
          <col style={{ width: 40 }} />
          <col style={{ width: 260 }} />
          {visibleColumns.map((k) => (
            <col
              key={k}
              style={{
                width: ["played", "won", "draw", "lost"].includes(k)
                  ? 48
                  : k === "goals"
                  ? 104
                  : 88,
              }}
            />
          ))}
          {showStatistics &&
            groups.flatMap((g, n) =>
              visibleMetrics.map((k) => (
                <col key={g + k} style={{ width: 132 }} />
              ))
            )}
          {onTeam && <col style={{ width: 120 }} />}
        </colgroup>
        <thead>
          <tr>
            <th
              rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
              className="tl-position"
            >
              #
            </th>
            <th
              rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
              className="tl-team"
            >
              {t("Mannschaft", "Team")}
            </th>
            {visibleColumns.map((k) => (
              <th
                rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
                key={k}
                title={labels[k]}
                className={columnClass(k)}
              >
                {{
                  played: t("Sp", "P"),
                  won: t("S", "W"),
                  draw: t("R", "D"),
                  lost: t("N", "L"),
                }[k] || labels[k]}
              </th>
            ))}
            {showStatistics &&
              visibleMetrics.length > 0 &&
              groups.map((label, n) => (
                <th
                  key={label}
                  colSpan={visibleMetrics.length}
                  className={`tl-stat-group tl-stat-${n}`}
                >
                  {label}
                </th>
              ))}
            {onTeam && (
              <th
                rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
                className="tl-open"
              >
                {t("Öffnen", "Open")}
              </th>
            )}
          </tr>
          {showStatistics && visibleMetrics.length > 0 && (
            <tr>
              {groups.map((label, n) =>
                visibleMetrics.map((k, i) => (
                  <th
                    key={label + k}
                    className={`tl-stat tl-stat-${n} ${
                      i === 0 ? "tl-stat-start" : ""
                    }`}
                  >
                    {k === "quotient" ? (
                      <>
                        {t("Spielstärke", "Strength")}
                        <small>{t("Quotient", "Ratio")}</small>
                      </>
                    ) : (
                      <>
                        {t("Torniveau", "Goal level")}
                        <small>{t("Toreschnitt", "Goals/game")}</small>
                      </>
                    )}
                  </th>
                ))
              )}
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.teamId}
              data-team-id={r.teamId}
              className={
                Number(selectedTeam) === r.teamId ? "tl-selected" : undefined
              }
              style={{
                borderLeft: "4px solid " + ruleAccent(rulesFor(r.teamId)),
              }}
              title={rulesFor(r.teamId).map(standingRuleLabel).join(" · ")}
            >
              <td className="tl-position">{fmt(r.position, 0)}</td>
              <th scope="row" className="tl-team">
                {data.team_images?.[r.teamId] ? (
                  <img src={data.team_images[r.teamId]} alt="" />
                ) : (
                  <span aria-hidden="true">⚽ </span>
                )}
                {r.name || t("Name nicht verfügbar", "Name unavailable")}
                {(data.transitions || []).some(
                  (x) =>
                    Number(x.team_id) === r.teamId &&
                    x.transition_type === "promoted"
                ) && (
                  <span
                    title={t(
                      "Aufsteiger aus der Vorsaison",
                      "Promoted from previous season"
                    )}
                  >
                    {" "}
                    ↑
                  </span>
                )}
                {(data.transitions || []).some(
                  (x) =>
                    Number(x.team_id) === r.teamId &&
                    x.transition_type === "relegated"
                ) && (
                  <span
                    title={t(
                      "Absteiger aus der Vorsaison",
                      "Relegated from previous season"
                    )}
                  >
                    {" "}
                    ↓
                  </span>
                )}
                {r.coverage && (
                  <small style={{ display: "block" }}>{r.coverage}</small>
                )}
              </th>
              {visibleColumns.map((k) => (
                <td key={k} className={columnClass(k)}>
                  {k === "goals"
                    ? formatGoalPair(r.goalsScored, r.goalsAgainst, (v) =>
                        fmt(v, digits(k))
                      )
                    : k === "difference"
                    ? signedDifference(r[k], digits(k), en ? "en-GB" : "de-DE")
                    : fmt(r[k], digits(k))}
                </td>
              ))}
              {showStatistics &&
                groups.map((label, n) =>
                  visibleMetrics.map((k, i) => {
                    const value =
                      n === 0
                        ? r[k]
                        : r[n === 1 ? "result" : "statistics"]?.[
                            k === "quotient" ? "total_strength" : "total_goals"
                          ];
                    const zeroAgainst =
                      n === 0 && k === "quotient" && r.goalsAgainst === 0;
                    return (
                      <td
                        key={label + k}
                        className={`tl-stat tl-stat-${n} ${
                          i === 0 ? "tl-stat-start" : ""
                        }`}
                        title={
                          zeroAgainst
                            ? t(
                                "Keine Gegentore: Quotient nicht definiert.",
                                "No goals conceded: ratio undefined."
                              )
                            : undefined
                        }
                      >
                        {zeroAgainst
                          ? t("— (0 Gegentore)", "— (0 conceded)")
                          : fmt(value, 3)}
                      </td>
                    );
                  })
                )}
              {onTeam && (
                <td className="tl-open">
                  <Button
                    disabled={!onTeam}
                    onClick={() => {
                      onTeam?.(r.teamId);
                    }}
                  >
                    {t("Team ansehen", "View team")}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
